import { useState, useMemo, useCallback } from "react";
import { useCreateOrderingPacket } from "@/hooks/useCreateOrderingPacket";
import { useAppData } from "@/data/AppDataContext";
import { useWarehouseInventory } from "@/data/WarehouseInventoryContext";
import { JobMaterialsModal, computeJobMaterialStatus } from "@/components/JobMaterialsModal";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { runAction, type ExecutableAction } from "@/lib/actionRunner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RoleContextBanner } from "@/components/RoleContextBanner";
import { RoleHandoffInbox } from "@/components/RoleHandoffInbox";
import { OrderingModal } from "@/components/OrderingModal";
import { IntakeReviewWorkspace } from "@/components/IntakeReviewWorkspace";

import { useBuildReviewActions } from "@/hooks/useBuildReviewActions";
import { MaterialPlanningPanel } from "@/components/MaterialPlanningPanel";
import { getInstallReadiness } from "@/components/JobWorkflowStatus";
import { getJobHealth } from "@/data/jobHealth";
import { format, addDays, parseISO, differenceInDays } from "date-fns";
import { BranchRegionFilter, EMPTY_BR, filterByBranchRegion, type BranchRegionState } from "@/components/BranchRegionFilter";
import {
  AlertTriangle, ArrowRight, ArrowUpRight, CheckCircle, XCircle, Clock,
  Shield, Package, Zap, Wrench, Calendar, Users, Ban, DollarSign, List, Phone,
  ShoppingCart,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { Job, Issue, MaterialEvent } from "@/data/types";
import { deriveBuildReviewStatus } from "@/data/buildReviewTypes";
import { derivePreSpendGate } from "@/data/preSpendGate";
import { getJobNextAction } from "@/data/nextActionEngine";
import { MyScheduleWidget } from "@/components/MyScheduleWidget";
import { useUISkin } from "@/data/UISkinContext";
import { CockpitHeader, SectionHeader, StatStrip, type StatItem } from "@/components/dashboard/primitives";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";
import { JobService } from "@/services/job.service";

const TODAY = new Date();
const TODAY_STR = format(TODAY, "yyyy-MM-dd");
const TOMORROW_STR = format(addDays(TODAY, 1), "yyyy-MM-dd");
const DAY_MS = 86_400_000;

type ReadinessFilter = "all" | "ready" | "partial" | "not-ready";

type DisplayGroup =
  | "blocked-today"
  | "blocked-atrisk"
  | "blocked-unscheduled"
  | "today"
  | "tomorrow"
  | "upcoming"
  | "unscheduled";

type ScheduleGroup = "today" | "tomorrow" | "upcoming" | "unscheduled";

interface ActionItem {
  id: string;
  jobId: string;
  title: string;
  summary: string;
  whyItMatters: string;
  severity: "critical" | "warning";
  scheduledDate?: string;
  displayGroup: DisplayGroup;
  installTiming: string;
  isBlocked: boolean;
  blockers: string[];
  revenueAtRisk: number;
  primaryAction: { label: string; link: string };
  secondaryAction?: { label: string; link: string };
  unscheduledReason?: string;
  daysSinceReady?: number;
}

const DISPLAY_GROUP_CONFIG: Record<DisplayGroup, { emoji: string; label: string; color: string; dotColor: string; badgeBg: string; badgeText: string }> = {
  "blocked-today":       { emoji: "🚫", label: "BLOCKED – TODAY",        color: "text-destructive",      dotColor: "bg-destructive",      badgeBg: "bg-destructive/15", badgeText: "text-destructive" },
  "blocked-atrisk":      { emoji: "🚫", label: "BLOCKED – AT RISK",     color: "text-destructive",      dotColor: "bg-destructive",      badgeBg: "bg-destructive/15", badgeText: "text-destructive" },
  "blocked-unscheduled": { emoji: "🚫", label: "BLOCKED – UNSCHEDULED", color: "text-destructive",      dotColor: "bg-destructive/60",   badgeBg: "bg-destructive/10", badgeText: "text-destructive" },
  today:                 { emoji: "🔴", label: "TODAY",                  color: "text-destructive",      dotColor: "bg-destructive",      badgeBg: "bg-destructive/15", badgeText: "text-destructive" },
  tomorrow:              { emoji: "🟠", label: "TOMORROW",               color: "text-warning",          dotColor: "bg-warning",          badgeBg: "bg-warning/15",     badgeText: "text-warning" },
  upcoming:              { emoji: "⚪", label: "UPCOMING",               color: "text-muted-foreground", dotColor: "bg-muted-foreground", badgeBg: "bg-muted/50",       badgeText: "text-muted-foreground" },
  unscheduled:           { emoji: "⚫", label: "UNSCHEDULED",            color: "text-destructive",      dotColor: "bg-destructive/60",   badgeBg: "bg-destructive/10", badgeText: "text-destructive" },
};

const DISPLAY_ORDER: DisplayGroup[] = [
  "blocked-today", "blocked-atrisk", "blocked-unscheduled",
  "today", "tomorrow", "upcoming", "unscheduled",
];

const GROUP_PRIORITY: Record<DisplayGroup, number> = {
  "blocked-today": 0, "blocked-atrisk": 1, "blocked-unscheduled": 2,
  today: 3, tomorrow: 4, upcoming: 5, unscheduled: 6,
};

const TIMELINE_GROUP_CONFIG: Record<ScheduleGroup, { emoji: string; label: string; color: string; dotColor: string; badgeBg: string; badgeText: string }> = {
  today:       { emoji: "🔴", label: "TODAY",        color: "text-destructive",      dotColor: "bg-destructive",      badgeBg: "bg-destructive/15", badgeText: "text-destructive" },
  tomorrow:    { emoji: "🟠", label: "TOMORROW",     color: "text-warning",          dotColor: "bg-warning",          badgeBg: "bg-warning/15",     badgeText: "text-warning" },
  upcoming:    { emoji: "⚪", label: "UPCOMING",     color: "text-muted-foreground", dotColor: "bg-muted-foreground", badgeBg: "bg-muted/50",       badgeText: "text-muted-foreground" },
  unscheduled: { emoji: "⚫", label: "UNSCHEDULED",  color: "text-destructive",      dotColor: "bg-destructive/60",   badgeBg: "bg-destructive/10", badgeText: "text-destructive" },
};

interface EnrichedJob {
  job: Job;
  openIssues: Issue[];
  readiness: ReturnType<typeof getInstallReadiness>;
  health: ReturnType<typeof getJobHealth>;
  scheduleGroup: ScheduleGroup;
  daysUntil: number;
  installTiming: string;
  isBlocked: boolean;
  needsAction: boolean;
  blockReasons: string[];
  revenueAtRisk: number;
  unscheduledReason: string;
  daysSinceReady: number;
}

function getUnscheduledReason(job: Job): string {
  if (job.assignmentType === "Unassigned") return "No crew assigned";
  if (job.materialReadiness === "Missing Items" || job.materialReadiness === "Not Ordered") return "Materials not available";
  if (job.permitRequired && job.permitStatus !== "Approved" && job.permitStatus !== "Not Required") return "Permit pending";
  return "Awaiting scheduling";
}

function getDaysSinceReady(job: Job): number {
  if (job.activityHistory && job.activityHistory.length > 0) {
    const oldest = job.activityHistory.reduce((a, b) => new Date(a.date) < new Date(b.date) ? a : b);
    return Math.max(0, differenceInDays(TODAY, parseISO(oldest.date)));
  }
  return 0;
}

function getContextAwareAction(job: Job, blockReasons: string[], openIssues: Issue[]): { label: string; link: string } {
  // Pre-spend gate: if Project Review / Rescission / Financial isn't cleared,
  // the only legitimate action is finishing the review — never Order / Permit /
  // Crew / Schedule (all four obligate company money or labor).
  const gate = derivePreSpendGate(job);
  if (!gate.cleared) {
    return { label: "Complete Project Review", link: "/intake-review" };
  }

  const next = getJobNextAction(job, openIssues);
  if (next) {
    if (next.icon === "materials") {
      return {
        label: next.cta || "Generate PO",
        link: next.executionMode === "navigate"
          ? `/ordering-queue?job=${job.id}`
          : "#order",
      };
    }
    if (next.icon === "permit") {
      return { label: next.cta || "Submit Permit", link: next.link || "/permitting" };
    }
    if (next.icon === "issue") {
      return { label: next.cta || "Resolve Issue", link: next.link || "/issues" };
    }
    if (next.icon === "crew" || next.icon === "dispatch") {
      return { label: next.cta || "Assign Crew", link: next.link || `/jobs/${job.id}` };
    }
    if (next.icon === "schedule") {
      return { label: next.cta || "Schedule Install", link: next.link || "/scheduling" };
    }
  }

  const hasMaterialsDefined = job.materialsList && job.materialsList.length > 0;
  const needsMaterials = blockReasons.some(r => r.includes("Materials missing")) || job.materialReadiness === "Missing Items";
  const notOrdered = blockReasons.some(r => r.includes("Materials not ordered")) || job.materialReadiness === "Not Ordered";

  if (needsMaterials || notOrdered) {
    return { label: "Order Materials", link: "#order" };
  }
  if (blockReasons.some(r => r.includes("Permit")) || (job.permitRequired && job.permitStatus !== "Approved" && job.permitStatus !== "Not Required")) {
    return { label: "Submit Permit", link: "/permitting" };
  }
  if (blockReasons.some(r => r.includes("crew")) || job.assignmentType === "Unassigned") {
    return { label: "Assign Crew", link: `/jobs/${job.id}` };
  }
  if (!job.scheduledInstallDate) {
    return { label: "Schedule Install", link: "/scheduling" };
  }
  if (openIssues.length > 0) {
    return { label: "Resolve Issue", link: "/issues" };
  }
  return { label: "Open Job", link: `/jobs/${job.id}` };
}

function getContextAwareSecondary(job: Job, blockReasons: string[]): { label: string; link: string } {
  if (blockReasons.some(r => r.includes("Materials"))) return { label: "View Warehouse", link: "/warehouse" };
  if (blockReasons.some(r => r.includes("Permit"))) return { label: "View Permit", link: "/permitting" };
  if (blockReasons.some(r => r.includes("crew"))) return { label: "View Scheduling", link: "/scheduling" };
  return { label: "Open Job", link: `/jobs/${job.id}` };
}

export function OperationsCommandCenter({ embedded = false }: { embedded?: boolean } = {}) {
  const { jobs: allJobs, issues: allIssues, credits, materialEvents, setMaterialEvents, buildReviewEvents, setBuildReviewEvents, setJobs } = useAppData();
  const [brFilter, setBrFilter] = useState<BranchRegionState>(EMPTY_BR);
  const [readinessFilter, setReadinessFilter] = useState<ReadinessFilter>("all");
  const [callConfirm, setCallConfirm] = useState<{ name: string; phone: string } | null>(null);
   const [orderingJob, setOrderingJob] = useState<Job | null>(null);
   const [materialsJob, setMaterialsJob] = useState<Job | null>(null);
  const { receivingRecords, jobAllocations } = useWarehouseInventory();
  const navigate = useNavigate();
  const buildReview = useBuildReviewActions();
  const { skin } = useUISkin();
  const isCockpit = skin === "cockpit";

  /**
   * Action shim resolver — converts a legacy { label, link } primaryAction into
   * an ExecutableAction. "Order Materials" → ordering modal. "Complete Project
   * Review" → IntakeReviewWorkspace (push mode), so operator stays in context.
   */
  const resolvePrimaryAction = useCallback(
    (job: Job, primary: { label: string; link: string }): ExecutableAction => {
      if (primary.label === "Order Materials") {
        return {
          label: primary.label,
          executionMode: "modal",
          openModal: () => setOrderingJob(job),
        };
      }
      if (primary.label === "Complete Project Review") {
        return {
          label: primary.label,
          executionMode: "modal",
          openModal: () => buildReview.openReview(job),
        };
      }
      return { label: primary.label, executionMode: "navigate", route: primary.link };
    },
    [buildReview],
  );

  const handlePrimaryClick = useCallback(
    async (e: React.MouseEvent, job: Job, primary: { label: string; link: string }) => {
      const action = resolvePrimaryAction(job, primary);
      // Only intercept when we have a non-navigate handler; otherwise let <Link> work natively.
      if (action.executionMode !== "navigate") {
        e.preventDefault();
        e.stopPropagation();
        const handled = await runAction(action, { navigate });
        if (!handled) navigate(primary.link); // safety fallback
      } else {
        e.stopPropagation();
      }
    },
    [resolvePrimaryAction, navigate],
  );

  const jobs = filterByBranchRegion(allJobs, brFilter);
  const issues = brFilter.branch === "all" && brFilter.region === "all"
    ? allIssues
    : allIssues.filter((i) => {
        const job = allJobs.find((j) => j.id === i.jobId);
        if (!job) return true;
        if (brFilter.branch !== "all" && job.branch !== brFilter.branch) return false;
        if (brFilter.region !== "all" && job.region !== brFilter.region) return false;
        return true;
      });

  const openIssues = issues.filter((i) => i.status === "Open" || i.status === "In Progress");

  const enrichedJobs = useMemo(() => {
    const openJobs = jobs.filter((j) => j.currentStage !== "Completed");
    return openJobs.map((j): EnrichedJob => {
      const jobIssues = openIssues.filter((i) => i.jobId === j.id);
      const readiness = getInstallReadiness(j, jobIssues, buildReviewEvents);
      const health = getJobHealth(j, jobIssues, credits);
      const revenueAtRisk = getJobDisplayValue(j);

      let daysUntil = -999;
      let scheduleGroup: ScheduleGroup = "unscheduled";
      let installTiming = "Not Scheduled";
      if (j.scheduledInstallDate) {
        const d = parseISO(j.scheduledInstallDate);
        daysUntil = Math.round((d.getTime() - TODAY.getTime()) / DAY_MS);
        if (j.scheduledInstallDate === TODAY_STR) {
          scheduleGroup = "today";
          installTiming = "Install Today";
        } else if (j.scheduledInstallDate === TOMORROW_STR) {
          scheduleGroup = "tomorrow";
          installTiming = "Install Tomorrow";
        } else if (daysUntil >= 0) {
          scheduleGroup = "upcoming";
          installTiming = `Installs in ${daysUntil}d`;
        }
      }

      const blockReasons: string[] = [];
      if (j.materialReadiness === "Missing Items") blockReasons.push("Materials missing");
      if (j.materialReadiness === "Not Ordered" && scheduleGroup !== "unscheduled") blockReasons.push("Materials not ordered");
      if (j.permitRequired && j.permitStatus === "Revision Needed") blockReasons.push("Permit rejected");
      if (j.permitRequired && j.permitStatus === "Submitted") blockReasons.push("Permit pending");
      if (j.assignmentType === "Unassigned" && scheduleGroup !== "unscheduled") blockReasons.push("No crew assigned");
      if (j.dispatchStatus === "Rejected") blockReasons.push("Contractor rejected");

      const isBlocked = blockReasons.length > 0;
      const needsAction = readiness.status !== "Ready for Install" || jobIssues.length > 0 || isBlocked;

      return {
        job: j, openIssues: jobIssues, readiness, health,
        scheduleGroup, daysUntil, installTiming, isBlocked, needsAction, blockReasons,
        revenueAtRisk,
        unscheduledReason: getUnscheduledReason(j),
        daysSinceReady: getDaysSinceReady(j),
      };
    });
  }, [jobs, openIssues, credits]);

  const metrics = useMemo(() => {
    const todayJobs = enrichedJobs.filter((e) => e.scheduleGroup === "today");
    const next48 = enrichedJobs.filter((e) => e.job.scheduledInstallDate && e.daysUntil >= 0 && e.daysUntil <= 2);
    const atRisk = next48.filter((e) => e.readiness.status !== "Ready for Install" || e.openIssues.length > 0);
    const notReady = enrichedJobs.filter((e) => e.readiness.status === "Not Ready");
    const blockedCount = enrichedJobs.filter((e) => e.isBlocked).length;
    const issueCount = enrichedJobs.reduce((sum, e) => sum + e.openIssues.length, 0);

    return { todayCount: todayJobs.length, atRiskCount: atRisk.length, notReadyCount: notReady.length, blockedCount, openIssueCount: issueCount };
  }, [enrichedJobs]);

  const revenueImpact = useMemo(() => {
    const blocked = enrichedJobs.filter(e => e.isBlocked);
    const atRisk48 = enrichedJobs.filter(e => e.job.scheduledInstallDate && e.daysUntil >= 0 && e.daysUntil <= 2 && e.needsAction);
    const unscheduled = enrichedJobs.filter(e => e.scheduleGroup === "unscheduled");

    return {
      blocked: blocked.reduce((s, e) => s + e.revenueAtRisk, 0),
      atRisk: atRisk48.reduce((s, e) => s + e.revenueAtRisk, 0),
      unscheduled: unscheduled.reduce((s, e) => s + e.revenueAtRisk, 0),
      unscheduledCount: unscheduled.length,
    };
  }, [enrichedJobs]);

  const readinessBuckets = useMemo(() => {
    const buckets = { ready: 0, partial: 0, notReady: 0 };
    enrichedJobs.forEach((e) => {
      if (e.readiness.status === "Ready for Install") buckets.ready++;
      else if (e.readiness.status === "Partially Ready") buckets.partial++;
      else buckets.notReady++;
    });
    return buckets;
  }, [enrichedJobs]);

  const actionItems = useMemo(() => {
    const items: ActionItem[] = [];

    enrichedJobs.forEach((e) => {
      if (!e.needsAction) return;

      const { job: j } = e;
      const blockers: string[] = [...e.blockReasons];
      if (e.openIssues.length > 0) blockers.push(`${e.openIssues.length} open issue${e.openIssues.length > 1 ? "s" : ""}`);
      if (blockers.length === 0) blockers.push("Readiness incomplete");

      const primaryAction = getContextAwareAction(j, e.blockReasons, e.openIssues);
      const secondaryAction = getContextAwareSecondary(j, e.blockReasons);

      const severity: "critical" | "warning" =
        e.isBlocked || e.scheduleGroup === "today" || e.scheduleGroup === "tomorrow" || e.health.status === "Problem"
          ? "critical" : "warning";

      let displayGroup: DisplayGroup;
      if (e.isBlocked) {
        if (e.scheduleGroup === "today") displayGroup = "blocked-today";
        else if (e.scheduleGroup === "tomorrow" || (e.daysUntil >= 0 && e.daysUntil <= 2)) displayGroup = "blocked-atrisk";
        else if (e.scheduleGroup === "unscheduled") displayGroup = "blocked-unscheduled";
        else displayGroup = "blocked-atrisk";
      } else {
        displayGroup = e.scheduleGroup;
      }

      let whyItMatters: string;
      if (displayGroup === "blocked-today") {
        whyItMatters = `Install will fail today — $${e.revenueAtRisk.toLocaleString()} lost if unresolved.`;
      } else if (displayGroup === "blocked-atrisk") {
        whyItMatters = `$${e.revenueAtRisk.toLocaleString()} at risk within 48h — blocked until resolved.`;
      } else if (displayGroup === "blocked-unscheduled") {
        whyItMatters = `$${e.revenueAtRisk.toLocaleString()} sitting blocked and unscheduled — delaying revenue.`;
      } else if (e.scheduleGroup === "today") {
        whyItMatters = `Install today — $${e.revenueAtRisk.toLocaleString()} fails if not resolved now.`;
      } else if (e.scheduleGroup === "tomorrow") {
        whyItMatters = `Job at risk tomorrow — $${e.revenueAtRisk.toLocaleString()} needs resolution today.`;
      } else if (e.scheduleGroup === "upcoming") {
        whyItMatters = `$${e.revenueAtRisk.toLocaleString()} — must resolve before install in ${e.daysUntil}d.`;
      } else {
        const daysNote = e.daysSinceReady > 0 ? ` (${e.daysSinceReady}d waiting)` : "";
        whyItMatters = `$${e.revenueAtRisk.toLocaleString()} unscheduled${daysNote} — revenue delayed.`;
      }

      items.push({
        id: `ops-${j.id}`,
        jobId: j.id,
        title: `${j.customerName} – ${j.address.city || "Unknown"} – ${blockers[0]}`,
        summary: `${j.address.street || ""} • Job #${j.jobNumber || j.id} • ${e.installTiming}`,
        whyItMatters,
        severity,
        scheduledDate: j.scheduledInstallDate,
        displayGroup,
        installTiming: e.installTiming,
        isBlocked: e.isBlocked,
        blockers,
        revenueAtRisk: e.revenueAtRisk,
        primaryAction,
        secondaryAction,
        unscheduledReason: e.scheduleGroup === "unscheduled" ? e.unscheduledReason : undefined,
        daysSinceReady: e.scheduleGroup === "unscheduled" ? e.daysSinceReady : undefined,
      });
    });

    return items.sort((a, b) => {
      if (a.displayGroup !== b.displayGroup) return GROUP_PRIORITY[a.displayGroup] - GROUP_PRIORITY[b.displayGroup];
      if (a.severity !== b.severity) return a.severity === "critical" ? -1 : 1;
      return b.revenueAtRisk - a.revenueAtRisk;
    }).slice(0, 15);
  }, [enrichedJobs]);

  const filteredItems = useMemo(() => {
    if (readinessFilter === "all") return actionItems;
    return actionItems.filter((item) => {
      const e = enrichedJobs.find((ej) => ej.job.id === item.jobId);
      if (!e) return readinessFilter === "not-ready";
      if (readinessFilter === "ready") return e.readiness.status === "Ready for Install";
      if (readinessFilter === "partial") return e.readiness.status === "Partially Ready";
      return e.readiness.status === "Not Ready";
    });
  }, [actionItems, readinessFilter, enrichedJobs]);

  type ExecutionLane = "handle-immediately" | "next-up" | "stuck-aging";

  const LANE_CONFIG: Record<ExecutionLane, { icon: React.ReactNode; label: string; sublabel: string; color: string; dotColor: string; badgeText: string; badgeBg: string; borderColor: string; bgColor: string }> = {
    "handle-immediately": {
      icon: <XCircle className="h-4 w-4 text-destructive" />,
      label: "Handle Immediately",
      sublabel: "Blocked today · Critical issues",
      color: "text-destructive",
      dotColor: "bg-destructive",
      badgeText: "text-destructive",
      badgeBg: "bg-destructive/10",
      borderColor: "border-destructive/20",
      bgColor: "bg-destructive/[0.03]",
    },
    "next-up": {
      icon: <AlertTriangle className="h-4 w-4 text-warning" />,
      label: "Next Up",
      sublabel: "48h risk · Tomorrow installs",
      color: "text-warning",
      dotColor: "bg-warning",
      badgeText: "text-warning",
      badgeBg: "bg-warning/10",
      borderColor: "border-warning/20",
      bgColor: "bg-warning/[0.02]",
    },
    "stuck-aging": {
      icon: <Clock className="h-4 w-4 text-muted-foreground" />,
      label: "Stuck / Aging",
      sublabel: "Unscheduled · Waiting on materials or permits",
      color: "text-muted-foreground",
      dotColor: "bg-muted-foreground",
      badgeText: "text-muted-foreground",
      badgeBg: "bg-muted/50",
      borderColor: "border-border",
      bgColor: "bg-muted/[0.03]",
    },
  };

  const executionLanes = useMemo(() => {
    const lanes: Record<ExecutionLane, ActionItem[]> = {
      "handle-immediately": [],
      "next-up": [],
      "stuck-aging": [],
    };

    filteredItems.forEach((item) => {
      if (
        item.displayGroup === "blocked-today" ||
        item.displayGroup === "today" ||
        (item.severity === "critical" && item.displayGroup !== "blocked-unscheduled" && item.displayGroup !== "unscheduled")
      ) {
        lanes["handle-immediately"].push(item);
      } else if (
        item.displayGroup === "blocked-atrisk" ||
        item.displayGroup === "tomorrow" ||
        item.displayGroup === "upcoming"
      ) {
        lanes["next-up"].push(item);
      } else {
        lanes["stuck-aging"].push(item);
      }
    });

    Object.values(lanes).forEach((arr) => arr.sort((a, b) => b.revenueAtRisk - a.revenueAtRisk));

    return lanes;
  }, [filteredItems]);

  const laneRevenue = useMemo(() => {
    const totals: Record<ExecutionLane, number> = { "handle-immediately": 0, "next-up": 0, "stuck-aging": 0 };
    (Object.keys(totals) as ExecutionLane[]).forEach((lane) => {
      totals[lane] = executionLanes[lane].reduce((s, i) => s + i.revenueAtRisk, 0);
    });
    return totals;
  }, [executionLanes]);

  const timeline = useMemo(() => {
    const todayJobs = enrichedJobs.filter((e) => e.scheduleGroup === "today");
    const tomorrowJobs = enrichedJobs.filter((e) => e.scheduleGroup === "tomorrow");
    const upcomingJobs = enrichedJobs.filter((e) => e.scheduleGroup === "upcoming");
    const unscheduledJobs = enrichedJobs.filter((e) => e.scheduleGroup === "unscheduled");
    return {
      today:       { jobs: todayJobs, count: todayJobs.length, hasBlockers: todayJobs.some((e) => e.isBlocked), actionNeeded: todayJobs.filter((e) => e.needsAction).length },
      tomorrow:    { jobs: tomorrowJobs, count: tomorrowJobs.length, hasRisk: tomorrowJobs.some((e) => e.needsAction), actionNeeded: tomorrowJobs.filter((e) => e.needsAction).length },
      upcoming:    { jobs: upcomingJobs, count: upcomingJobs.length, hasRisk: upcomingJobs.some((e) => e.needsAction && e.readiness.status === "Not Ready"), actionNeeded: upcomingJobs.filter((e) => e.needsAction).length },
      unscheduled: { jobs: unscheduledJobs, count: unscheduledJobs.length, hasCritical: unscheduledJobs.some((e) => e.openIssues.length > 0 || e.readiness.status !== "Ready for Install"), actionNeeded: unscheduledJobs.filter((e) => e.needsAction).length },
    };
  }, [enrichedJobs]);

  const quickActions = useMemo(() => {
    const materialJobs = enrichedJobs.filter(e => e.blockReasons.some(r => r.includes("Materials")) || e.job.materialReadiness === "Missing Items" || e.job.materialReadiness === "Not Ordered");
    const permitJobs = enrichedJobs.filter(e => e.blockReasons.some(r => r.includes("Permit")) || (e.job.permitRequired && e.job.permitStatus !== "Approved" && e.job.permitStatus !== "Not Required"));
    const crewJobs = enrichedJobs.filter(e => e.job.assignmentType === "Unassigned");
    const issueJobs = enrichedJobs.filter(e => e.openIssues.length > 0);
    return [
      { label: "Order Materials", count: materialJobs.length, icon: Package, link: "#order", color: "text-destructive", bg: "bg-destructive/10" },
      { label: "Submit Permits", count: permitJobs.length, icon: Shield, link: "/permitting", color: "text-warning", bg: "bg-warning/10" },
      { label: "Assign Crew", count: crewJobs.length, icon: Users, link: "/scheduling", color: "text-primary", bg: "bg-primary/10" },
      { label: "Resolve Issues", count: issueJobs.length, icon: Wrench, link: "/issues", color: "text-destructive", bg: "bg-destructive/10" },
    ].filter(a => a.count > 0);
  }, [enrichedJobs]);

  /* ── Ready for Ordering: approved jobs not yet ordered ── */
  const readyForOrdering = useMemo(() => {
    return enrichedJobs.filter(e => {
      return e.job.approvedForOrdering === true &&
        (e.job.planningStatus === "Ready for Ordering" || e.job.planningStatus === "Sent to Ordering");
    });
  }, [enrichedJobs]);

   const createOrderingPacket = useCreateOrderingPacket();
   const handleOrderingSubmit = useCallback(async (data: { jobId: string; vendor: string; orderType: "Batch" | "Direct" | "Rush"; notes: string }) => {
     const target = jobs.find((j) => j.id === data.jobId);
     if (!target) return;
     const patch: Partial<Job> = {
       materialReadiness: "Ordered" as any,
       planningStatus: "Ordered",
       currentStage: "Ordered" as any,
       selectedVendor: data.vendor,
       orderType: data.orderType === "Rush" ? "Emergency" : "Batch",
       activityHistory: [...target.activityHistory, {
         id: `ops-order-${Date.now()}`,
         date: new Date().toISOString(),
         action: `Materials ordered — ${data.vendor} (${data.orderType})`,
         user: "Ops Manager",
         noteType: "ops" as const,
       }],
     };
     const { data: persisted, error } = await JobService.update(target.id, patch);
     if (error || !persisted) {
       console.error("Failed to persist ordering update:", error);
       toast.error("Failed to place order");
       return;
     }
     setJobs(prev => prev.map(j => (j.id === persisted.id ? persisted : j)));
     const created = createOrderingPacket(data);
     if (created) {
       toast.success("Packet created — proceed to ordering queue", {
         description: `${data.vendor} — ${data.orderType}`,
       });
       navigate(`/ordering-queue?job=${data.jobId}`);
     } else {
       toast.success("Order placed", { description: `${data.vendor} — ${data.orderType}` });
     }
   }, [jobs, setJobs, createOrderingPacket, navigate]);

  // ── Compact card renderer ──
  const renderCard = (item: ActionItem) => {
    const cfg = DISPLAY_GROUP_CONFIG[item.displayGroup];
    const ej = enrichedJobs.find((e) => e.job.id === item.jobId);
    const phone = ej?.job.phone || "";
    const material = ej?.job.projectConfig?.bathroomMaterials?.[0] ?? ej?.job.projectConfig?.primaryMaterial ?? "";
    const rawProjectType = ej?.job.projectType || "";
    const displayProjectType = rawProjectType === "Tub to Shower Conversion" ? "Shower Conversion" : rawProjectType;
    const productLabel = [material, displayProjectType].filter(Boolean).join(" ");
    const customerName = ej?.job.customerName || "";
    const customerCity = item.title.split(" – ").slice(0, 2).join(" — ");

    const handlePhoneClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (phone) {
        setCallConfirm({ name: customerName, phone });
      }
    };

    return (
      <TooltipProvider key={item.id} delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={
              isCockpit
                ? `rounded-md border bg-card transition-colors cursor-default hover:bg-muted/20 ${
                    item.isBlocked
                      ? "border-destructive/30"
                      : item.severity === "critical"
                      ? "border-warning/30"
                      : "border-border/60"
                  }`
                : `rounded-lg border transition-all duration-200 cursor-default hover:-translate-y-[1px] hover:shadow-md ${
                    item.isBlocked
                      ? "border-destructive/20 bg-destructive/[0.03] hover:border-destructive/30"
                      : item.severity === "critical"
                      ? "border-warning/15 bg-warning/[0.02] hover:border-warning/25"
                      : "border-border/40 bg-card/60 hover:border-border/60"
                  }`
            }>
              <div className={isCockpit ? "px-3 py-2.5 space-y-1.5" : "px-4 py-3.5 space-y-2"}>
                {/* Row 1: Name — City + Phone */}
                <div className="flex items-center gap-2.5">
                  <p className={
                    isCockpit
                      ? "text-[13px] font-semibold text-foreground leading-tight truncate"
                      : "text-[14px] font-bold text-foreground leading-tight truncate"
                  }>
                    {customerCity}
                  </p>
                  {phone && (
                    <button
                      onClick={handlePhoneClick}
                      className="inline-flex items-center gap-1 text-[11px] text-muted-foreground/60 hover:text-primary hover:underline shrink-0 transition-colors"
                    >
                      <Phone className="h-3 w-3" />
                      {phone}
                    </button>
                  )}
                </div>

                {/* Row 2: Product + Issue */}
                <p className="text-[12px] leading-tight">
                  {productLabel && <span className="text-muted-foreground/60">{productLabel} • </span>}
                  <span className={`font-medium ${item.isBlocked ? "text-destructive" : cfg.badgeText}`}>
                    {item.blockers[0]}
                  </span>
                </p>

                {/* Row 3: Timing + $ + Action */}
                <div className="flex items-center justify-between pt-0.5">
                  <span className="text-[11px] text-muted-foreground/60 tabular-nums">
                    {item.installTiming} · <span className="text-muted-foreground/80">${item.revenueAtRisk.toLocaleString()}</span>
                  </span>
                  <Link
                    to={item.primaryAction.link}
                    onClick={(e) => {
                      const job = enrichedJobs.find((x) => x.job.id === item.jobId)?.job;
                      if (job) handlePrimaryClick(e, job, item.primaryAction);
                      else e.stopPropagation();
                    }}
                    className={
                      isCockpit
                        ? "shrink-0 inline-flex items-center px-2.5 py-1 text-[11px] font-medium rounded-md border border-border/60 bg-card text-foreground hover:bg-muted/40 transition-colors whitespace-nowrap"
                        : "shrink-0 inline-flex items-center px-3 py-1.5 text-[11px] font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors whitespace-nowrap"
                    }
                  >
                    {item.primaryAction.label}
                  </Link>
                </div>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs">
            <p className="text-xs font-semibold mb-1">{item.title}</p>
            <p className="text-[11px] text-muted-foreground mb-1">{item.whyItMatters}</p>
            {item.blockers.length > 1 && (
              <p className="text-[10px] text-muted-foreground">Blockers: {item.blockers.join(" · ")}</p>
            )}
            {item.secondaryAction && (
              <Link to={item.secondaryAction.link} className="text-[10px] text-primary hover:underline mt-1 inline-block">
                {item.secondaryAction.label} →
              </Link>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // ── Compact list strip for tabs ──
  const renderJobStrip = (e: EnrichedJob) => {
    const ai = actionItems.find((a) => a.jobId === e.job.id);
    const matStatus = computeJobMaterialStatus(receivingRecords, jobAllocations, e.job.id);
    const material = e.job.projectConfig?.bathroomMaterials?.[0] ?? "";
    const pt = e.job.projectType === "Tub to Shower Conversion" ? "Shower Conversion" : e.job.projectType;
    const productLabel = [material, pt].filter(Boolean).join(" ");
    return (
      <div key={e.job.id} className={`flex rounded-lg border overflow-hidden bg-card/60 transition-all hover:-translate-y-[0.5px] hover:shadow-sm ${e.isBlocked ? "border-destructive/25" : e.needsAction ? "border-warning/15" : "border-border/40"}`}>
        {/* Left accent bar */}
        <div className={`w-1 shrink-0 ${e.isBlocked ? "bg-destructive" : e.readiness.status === "Ready for Install" ? "bg-success" : e.readiness.status === "Partially Ready" ? "bg-warning/60" : "bg-destructive/60"}`} />
        <div className="flex-1 px-3 py-2.5">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-semibold truncate max-w-[220px] text-foreground">{e.job.customerName} — {e.job.address.city || "Unknown"}</span>
            {productLabel && <span className="text-[10px] text-muted-foreground/70 truncate max-w-[140px]">{productLabel}</span>}
            {e.blockReasons.length > 0 && <span className="text-[11px] font-semibold text-destructive shrink-0">{e.blockReasons[0]}</span>}
          </div>
          <div className="flex items-center gap-2.5 mt-1">
            <span className="text-[11px] text-muted-foreground">
              {e.installTiming} · {e.readiness.status}
              {e.scheduleGroup === "unscheduled" && e.daysSinceReady > 0 && ` · ${e.daysSinceReady}d waiting`}
              {" · "}<span className="font-semibold text-foreground">${e.revenueAtRisk.toLocaleString()}</span>
            </span>
            {/* Material + Install Readiness badges */}
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
              matStatus.materialStatus === "Staged" ? "bg-success/15 text-success" :
              matStatus.materialStatus === "Fully Received" ? "bg-info/15 text-info" :
              matStatus.materialStatus === "Partially Received" ? "bg-warning/15 text-warning" :
              matStatus.materialStatus === "Ordered" ? "bg-primary/15 text-primary" :
              "bg-muted text-muted-foreground"
            }`}>
              {matStatus.materialStatus}
            </span>
            {matStatus.installReady && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-success/15 text-success">✓ Install Ready</span>
            )}
            {/* Actions */}
            <button
              onClick={() => setMaterialsJob(e.job)}
              className="inline-flex items-center px-2 py-1 text-[10px] font-medium rounded-md border border-border text-muted-foreground hover:bg-muted/50 transition-colors whitespace-nowrap"
            >
              <Package className="h-2.5 w-2.5 mr-1" /> Materials
            </button>
            {ai ? (
              <Link
                to={ai.primaryAction.link}
                onClick={(ev) => handlePrimaryClick(ev, e.job, ai.primaryAction)}
                className="inline-flex items-center px-2.5 py-1 text-[10px] font-semibold rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors whitespace-nowrap"
              >
                {ai.primaryAction.label}
              </Link>
            ) : (
              <Link to={`/jobs/${e.job.id}`} className="inline-flex items-center px-2.5 py-1 text-[10px] font-medium rounded-md border border-border text-muted-foreground hover:bg-muted/50 transition-colors whitespace-nowrap">
                View
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─── Cockpit-skinned KPI items (used only when isCockpit) ───
  // Same numbers, redrawn as a hairline-divided StatStrip — no tinted cards.
  const cockpitKpiItems: StatItem[] = [
    { label: "Jobs Today", value: metrics.todayCount },
    { label: "Blocked", value: metrics.blockedCount, accent: metrics.blockedCount > 0 ? "bad" : "good" },
    { label: "At Risk 48h", value: metrics.atRiskCount, accent: metrics.atRiskCount > 0 ? "bad" : "good" },
    { label: "Not Ready", value: metrics.notReadyCount, accent: metrics.notReadyCount > 0 ? "bad" : "good" },
    { label: "Open Issues", value: metrics.openIssueCount, accent: metrics.openIssueCount > 0 ? "warn" : "good" },
    { label: "$ Blocked", value: `$${(revenueImpact.blocked / 1000).toFixed(0)}k`, accent: revenueImpact.blocked > 0 ? "bad" : undefined },
    { label: "$ At Risk", value: `$${(revenueImpact.atRisk / 1000).toFixed(0)}k`, accent: revenueImpact.atRisk > 0 ? "warn" : undefined },
    { label: "$ Unsched", value: `$${(revenueImpact.unscheduled / 1000).toFixed(0)}k` },
  ];

  return (
    <div className={
      embedded
        ? "space-y-5 w-full"
        : isCockpit
          ? "space-y-6 max-w-[1600px] mx-auto w-full"
          : "space-y-3 max-w-[1300px] mx-auto"
    }>
      {!embedded && <RoleContextBanner />}
      {!embedded && <RoleHandoffInbox />}

      {/* ═══ HEADER ═══ */}
      {embedded ? (
        // Embedded inside CommandDashboardLens — parent already framed the surface.
        // Single toolbar row: filters + calendar on the LEFT, "View in native
        // route" deep-link on the RIGHT. Lens body no longer renders its own row.
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <BranchRegionFilter value={brFilter} onChange={setBrFilter} jobs={allJobs} compact />
            <Link
              to="/scheduling"
              className="text-[12px] text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border/60 hover:bg-muted/30"
            >
              <Calendar className="h-3.5 w-3.5" /> Calendar
            </Link>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground transition-colors"
          >
            View in native route
            <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
      ) : isCockpit ? (
        <CockpitHeader
          title="Operations Command Center"
          subtitle={`Real-time execution surface · ${format(TODAY, "EEEE, MMMM d, yyyy")}`}
          right={
            <>
              <BranchRegionFilter value={brFilter} onChange={setBrFilter} jobs={allJobs} compact />
              <Link
                to="/scheduling"
                className="text-[13px] text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5 px-2.5 py-1.5"
              >
                <Calendar className="h-3.5 w-3.5" /> Calendar
              </Link>
            </>
          }
        />
      ) : (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
             <h1 className="text-[22px] font-extrabold tracking-[-0.02em] text-foreground">Operations Command Center</h1>
             <p className="text-[12px] text-muted-foreground/70 mt-0.5">
              Real-Time Execution Surface · {format(TODAY, "EEEE, MMMM d, yyyy")}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <BranchRegionFilter value={brFilter} onChange={setBrFilter} jobs={allJobs} compact />
            <Link to="/scheduling" className="text-[12px] font-medium text-primary hover:underline inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border hover:bg-muted/30 transition-colors">
              <Calendar className="h-3.5 w-3.5" /> Calendar
            </Link>
          </div>
        </div>
      )}

      {/* ═══ MY SCHEDULE ═══ — suppressed when embedded; leadership doesn't need a personal calendar */}
      {!embedded && <MyScheduleWidget compact />}
      {/* ═══ METRICS + REVENUE ═══ */}
      {isCockpit ? (
        <StatStrip items={cockpitKpiItems} />
      ) : (
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
          {[
            { label: "Jobs Today", value: metrics.todayCount, icon: Calendar, color: "text-primary", bg: "bg-primary/10 border-primary/20" },
            { label: "Blocked", value: metrics.blockedCount, icon: Ban, color: metrics.blockedCount > 0 ? "text-destructive" : "text-success", bg: metrics.blockedCount > 0 ? "bg-destructive/10 border-destructive/20" : "bg-success/10 border-success/20" },
            { label: "At Risk 48h", value: metrics.atRiskCount, icon: AlertTriangle, color: metrics.atRiskCount > 0 ? "text-destructive" : "text-success", bg: metrics.atRiskCount > 0 ? "bg-destructive/10 border-destructive/20" : "bg-success/10 border-success/20" },
            { label: "Not Ready", value: metrics.notReadyCount, icon: XCircle, color: metrics.notReadyCount > 0 ? "text-destructive" : "text-success", bg: metrics.notReadyCount > 0 ? "bg-destructive/10 border-destructive/20" : "bg-success/10 border-success/20" },
            { label: "Open Issues", value: metrics.openIssueCount, icon: Wrench, color: metrics.openIssueCount > 0 ? "text-warning" : "text-success", bg: metrics.openIssueCount > 0 ? "bg-warning/10 border-warning/20" : "bg-success/10 border-success/20" },
            { label: "$ Blocked", value: `$${(revenueImpact.blocked / 1000).toFixed(0)}k`, icon: DollarSign, color: "text-destructive", bg: "bg-destructive/5 border-destructive/15" },
            { label: "$ At Risk", value: `$${(revenueImpact.atRisk / 1000).toFixed(0)}k`, icon: DollarSign, color: "text-warning", bg: "bg-warning/5 border-warning/15" },
            { label: "$ Unsched", value: `$${(revenueImpact.unscheduled / 1000).toFixed(0)}k`, icon: DollarSign, color: "text-muted-foreground", bg: "bg-muted/30 border-border" },
          ].map((m) => (
            <Card key={m.label} className={`border ${m.bg} shadow-none`}>
              <CardContent className="px-2 py-3 text-center">
                <p className={`text-lg font-extrabold leading-tight ${m.color}`}>{m.value}</p>
                <p className="text-[9px] text-muted-foreground font-semibold uppercase tracking-wider leading-tight mt-1">{m.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ═══ QUICK ACTIONS BAR ═══ */}
      {quickActions.length > 0 && (
        <div className={isCockpit
          ? "flex items-center gap-1.5 flex-wrap border-t border-border/40 pt-3"
          : "flex items-center gap-2.5 flex-wrap"
        }>
          <span className={isCockpit
            ? "text-[10px] uppercase tracking-[0.08em] font-medium text-muted-foreground/70 mr-1"
            : "text-[9px] font-bold uppercase tracking-widest text-muted-foreground/50 mr-0.5"
          }>
            {isCockpit ? "Quick actions" : "Quick Actions:"}
          </span>
          {quickActions.map((a) => {
            const cockpitClass = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border/60 bg-card text-[12px] text-foreground hover:bg-muted/40 transition-colors";
            const classicClass = `inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-semibold transition-all hover:shadow-sm hover:-translate-y-[0.5px] ${a.bg} border-border`;
            const iconClass = isCockpit ? "h-3.5 w-3.5 text-muted-foreground" : `h-3.5 w-3.5 ${a.color}`;
            const countClass = isCockpit
              ? "text-[11px] tabular-nums text-muted-foreground/80"
              : `text-[11px] font-black ${a.color}`;
            if (a.link === "#order") {
              const firstJob = readyForOrdering[0]?.job;
              return (
                <button
                  key={a.label}
                  onClick={() => firstJob && setOrderingJob(firstJob)}
                  className={isCockpit ? cockpitClass : classicClass}
                >
                  <a.icon className={iconClass} />
                  {a.label}
                  <span className={countClass}>{isCockpit ? a.count : `(${a.count})`}</span>
                </button>
              );
            }
            return (
              <Link
                key={a.label}
                to={a.link}
                className={isCockpit ? cockpitClass : classicClass}
              >
                <a.icon className={iconClass} />
                {a.label}
                <span className={countClass}>{isCockpit ? a.count : `(${a.count})`}</span>
              </Link>
            );
          })}
        </div>
      )}

      {/* ═══ INTAKE REVIEW ALERT ═══ */}
      {(() => {
        const needsReviewCount = jobs.filter(j => {
          const jobEvents = buildReviewEvents.filter(e => e.jobId === j.id);
          return jobEvents.length === 0 && j.currentStage !== "Completed";
        }).length;
        const revisionCount = buildReviewEvents.filter((e, i, arr) => {
          const jobEvents = arr.filter(ev => ev.jobId === e.jobId);
          return jobEvents[jobEvents.length - 1]?.action === "revision_requested";
        }).length;
        const totalPending = needsReviewCount + revisionCount;
        if (totalPending === 0) return null;
        if (isCockpit) {
          // Cockpit: a quiet hairline row, not a tinted alert. Same info, less shouting.
          return (
            <Link
              to="/intake-review"
              className="flex items-center gap-3 px-3 py-2 rounded-md border border-border/60 bg-card hover:bg-muted/30 transition-colors"
            >
              <Shield className="h-3.5 w-3.5 text-warning shrink-0" />
              <div className="flex-1 min-w-0 flex items-baseline gap-2">
                <span className="text-[12px] font-medium text-foreground">Intake review required</span>
                <span className="text-[11px] text-muted-foreground/80">
                  {totalPending} job{totalPending !== 1 ? "s" : ""} pending before entering operations
                </span>
              </div>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/70" />
            </Link>
          );
        }
        return (
          <Link to="/intake-review" className="block">
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-warning/30 bg-warning/5 hover:bg-warning/10 transition-colors cursor-pointer">
              <div className="h-7 w-7 rounded-md bg-warning/15 flex items-center justify-center flex-shrink-0">
                <Shield className="h-3.5 w-3.5 text-warning" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-semibold text-foreground">Intake Review Required</p>
                <p className="text-[11px] text-muted-foreground">{totalPending} job{totalPending !== 1 ? "s" : ""} pending review before entering operations</p>
              </div>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
          </Link>
        );
      })()}

      {/* ═══ MATERIAL PLANNING ═══ */}
      {/* Push-Mode chain: Materials → Ordering. Confirming the plan
          immediately opens the Send-to-Ordering modal carrying the same
          job, so the operator stays in one continuous push session. */}
      <MaterialPlanningPanel onConfirmed={(j) => setOrderingJob(j)} />

      {/* ═══ READY FOR ORDERING ═══ */}
      {readyForOrdering.length > 0 && (
        <section className={isCockpit ? "" : "space-y-2.5"}>
          {isCockpit ? (
            <SectionHeader
              title="Ready for Ordering"
              eyebrow={`${readyForOrdering.length} approved · awaiting order`}
            />
          ) : (
            <div className="flex items-center gap-2.5">
              <ShoppingCart className="h-5 w-5 text-primary" />
              <h2 className="text-[16px] font-bold tracking-tight text-foreground">Ready for Ordering</h2>
              <span className="text-[11px] font-medium text-muted-foreground">
                {readyForOrdering.length} job{readyForOrdering.length !== 1 ? "s" : ""} approved — awaiting order
              </span>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {readyForOrdering.map(e => {
              const material = e.job.projectConfig?.bathroomMaterials?.[0] ?? "";
              const pt = e.job.projectType === "Tub to Shower Conversion" ? "Shower Conversion" : e.job.projectType;
              const productLabel = [material, pt].filter(Boolean).join(" ");
              const enclosure = e.job.projectConfig?.enclosureType || "";
              const scopeLabel = [productLabel, enclosure].filter(Boolean).join(" • ");

              return (
                <div key={e.job.id} className="rounded-lg border border-primary/20 bg-primary/[0.02] hover:border-primary/30 transition-all hover:-translate-y-[1px] hover:shadow-md">
                  <div className="px-4 py-3.5 space-y-2">
                    <div className="flex items-center gap-2.5">
                      <p className="text-[14px] font-bold text-foreground leading-tight truncate">
                        {e.job.customerName} — {e.job.address.city || "Unknown"}
                      </p>
                      {e.job.phone && (
                        <button
                          onClick={() => setCallConfirm({ name: e.job.customerName, phone: e.job.phone })}
                          className="inline-flex items-center gap-1 text-[11px] text-muted-foreground/60 hover:text-primary hover:underline shrink-0 transition-colors"
                        >
                          <Phone className="h-3 w-3" /> {e.job.phone}
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[12px] text-muted-foreground/60">{scopeLabel}</span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                        Approved for Ordering
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-0.5">
                      <span className="text-[11px] text-muted-foreground/50">
                        {e.installTiming} · <span className="font-semibold text-muted-foreground/70">${e.revenueAtRisk.toLocaleString()}</span>
                      </span>
                      <Button
                        size="sm"
                        className="h-7 text-[11px] font-semibold px-3 gap-1.5"
                        onClick={() => setOrderingJob(e.job)}
                      >
                        <ShoppingCart className="h-3 w-3" />
                        Order Materials
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ═══ TABS ═══ */}
      <Tabs defaultValue="action-now" className="space-y-3 mt-2">
        <TabsList className="w-full justify-start bg-muted/30 h-8">
          <TabsTrigger value="action-now" className="text-[11px] font-semibold gap-1.5 h-7">
            <Zap className="h-3 w-3" /> Action Now
          </TabsTrigger>
          <TabsTrigger value="upcoming" className="text-[11px] font-semibold gap-1.5 h-7">
            <Calendar className="h-3 w-3" /> Upcoming ({timeline.upcoming.count})
          </TabsTrigger>
          <TabsTrigger value="unscheduled" className="text-[11px] font-semibold gap-1.5 h-7">
            <Clock className="h-3 w-3" /> Unscheduled ({timeline.unscheduled.count})
          </TabsTrigger>
          <TabsTrigger value="all-jobs" className="text-[11px] font-semibold gap-1.5 h-7">
            <List className="h-3 w-3" /> All ({enrichedJobs.length})
          </TabsTrigger>
        </TabsList>

        {/* ── ACTION NOW TAB ── */}
        <TabsContent value="action-now">
          {filteredItems.length === 0 ? (
            <div className="text-center py-6">
              <CheckCircle className="h-6 w-6 mx-auto text-success mb-1" />
              <p className="text-sm font-medium text-success">All Clear</p>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Readiness filter inline */}
              <div className="flex items-center gap-2">
                {([
                  { key: "all" as ReadinessFilter, label: "All", count: actionItems.length },
                  { key: "not-ready" as ReadinessFilter, label: "Not Ready", count: readinessBuckets.notReady },
                  { key: "partial" as ReadinessFilter, label: "Partial", count: readinessBuckets.partial },
                  { key: "ready" as ReadinessFilter, label: "Ready", count: readinessBuckets.ready },
                ]).map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setReadinessFilter(f.key)}
                    className={`text-[10px] font-medium px-2.5 py-1.5 rounded-md border transition-colors ${
                      readinessFilter === f.key ? "bg-primary text-primary-foreground border-primary" : "bg-muted/20 text-muted-foreground/60 border-border/50 hover:bg-muted/40"
                    }`}
                  >
                    {f.label} ({f.count})
                  </button>
                ))}
              </div>

              {(["handle-immediately", "next-up", "stuck-aging"] as ExecutionLane[]).map((lane) => {
                const items = executionLanes[lane];
                if (items.length === 0) return null;
                const cfg = LANE_CONFIG[lane];
                const rev = laneRevenue[lane];
                const visible = items.slice(0, 5);
                const remaining = items.length - 5;
                return (
                    <div key={lane} className="space-y-3">
                     {/* Section header */}
                     <div className="flex items-center gap-2 px-0.5">
                       {cfg.icon}
                       <h3 className={
                         isCockpit
                           ? `text-[13px] font-semibold tracking-tight ${cfg.color}`
                           : `${lane === "handle-immediately" ? "text-[16px]" : "text-[15px]"} font-bold tracking-tight ${cfg.color}`
                       }>{cfg.label}</h3>
                      <span className={
                        isCockpit
                          ? "text-[11px] text-muted-foreground/70 tabular-nums"
                          : "text-[12px] text-muted-foreground/40 font-medium"
                      }>
                        •  {items.length} job{items.length !== 1 ? "s" : ""}
                        {rev > 0 && ` •  $${rev.toLocaleString()} at risk`}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {visible.map(renderCard)}
                    </div>
                    {remaining > 0 && (
                      <button className="text-[11px] text-primary hover:underline font-semibold inline-flex items-center gap-1 px-0.5">
                        View All ({items.length}) <ArrowRight className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── UPCOMING TAB ── */}
        <TabsContent value="upcoming">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="h-3.5 w-3.5 text-primary" />
            <h2 className="text-[13px] font-bold">Upcoming Jobs</h2>
            <span className="text-[10px] text-muted-foreground">{timeline.upcoming.actionNeeded} need action</span>
          </div>
          {timeline.upcoming.jobs.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No upcoming jobs</p>
          ) : (
            <div className="space-y-1.5">
              {timeline.upcoming.jobs.map(renderJobStrip)}
            </div>
          )}
        </TabsContent>

        {/* ── UNSCHEDULED TAB ── */}
        <TabsContent value="unscheduled">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-3.5 w-3.5 text-destructive" />
            <h2 className="text-[13px] font-bold">Unscheduled Jobs</h2>
            {timeline.unscheduled.count > 0 && (
              <div className="flex items-center gap-3 ml-auto text-[10px]">
                <span className="font-bold text-destructive">{timeline.unscheduled.count} jobs</span>
                <span className="font-bold text-destructive">${revenueImpact.unscheduled.toLocaleString()}</span>
              </div>
            )}
          </div>
          {timeline.unscheduled.jobs.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No unscheduled jobs</p>
          ) : (
            <div className="space-y-1.5">
              {timeline.unscheduled.jobs.map(renderJobStrip)}
            </div>
          )}
        </TabsContent>

        {/* ── ALL JOBS TAB ── */}
        <TabsContent value="all-jobs">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <List className="h-3.5 w-3.5 text-primary" />
              <h2 className="text-[13px] font-bold">All Active Jobs ({enrichedJobs.length})</h2>
            </div>
            <Link to="/jobs" className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5">
              Full View <ArrowRight className="h-2.5 w-2.5" />
            </Link>
          </div>
          <div className="space-y-1.5">
            {enrichedJobs
              .sort((a, b) => {
                const gp = (e: EnrichedJob) => e.isBlocked ? 0 : e.scheduleGroup === "today" ? 1 : e.scheduleGroup === "tomorrow" ? 2 : e.scheduleGroup === "upcoming" ? 3 : 4;
                return gp(a) - gp(b) || b.revenueAtRisk - a.revenueAtRisk;
              })
              .map(renderJobStrip)}
          </div>
        </TabsContent>
      </Tabs>

      {/* ═══ CALL CONFIRMATION DIALOG ═══ */}
      <AlertDialog open={!!callConfirm} onOpenChange={(open) => !open && setCallConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Call {callConfirm?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Initiate outgoing call to {callConfirm?.phone}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              toast.success(`Calling ${callConfirm?.name}`, { description: callConfirm?.phone });
              setCallConfirm(null);
            }}>
              Call Now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ═══ ORDERING MODAL ═══ */}
      {orderingJob && (
        <OrderingModal
          open={!!orderingJob}
          onOpenChange={(open) => { if (!open) setOrderingJob(null); }}
          job={orderingJob}
          onSendToOrdering={handleOrderingSubmit}
        />
      )}

      {/* PUSH-MODE chain: after packet creation, user is navigated to the ordering queue to manage placement */}

      {/* ═══ JOB MATERIALS MODAL ═══ */}
      {materialsJob && (
        <JobMaterialsModal
          open={!!materialsJob}
          onOpenChange={(open) => { if (!open) setMaterialsJob(null); }}
          job={materialsJob}
        />
      )}

      {/* ═══ BUILD REVIEW WORKSPACE (push mode) ═══ */}
      <IntakeReviewWorkspace
        job={buildReview.reviewJob}
        open={!!buildReview.reviewJob}
        lane="needs-review"
        buildReviewEvents={buildReviewEvents}
        openIssues={allIssues}
        onClose={buildReview.closeReview}
        onApprove={buildReview.onApprove}
        onApproveAndContinue={buildReview.commitApproval}
        onContinueChain={(approvedJob, step) => {
          buildReview.closeReview();
          if (step.kind === "done") return;
          if (step.kind === "generate_po") { setOrderingJob(approvedJob); return; }
          // No local modal for these on this surface — fall through to job detail.
          if (step.kind === "view_permit" || step.kind === "assign_crew" || step.kind === "reschedule" || step.kind === "resolve_issue") {
            navigate(`/jobs/${approvedJob.id}`);
            return;
          }
          if (step.kind === "navigate") navigate(step.to);
        }}
        onRequestRevision={buildReview.onRequestRevision}
        onSendToSales={buildReview.onSendToSales}
        onSalesApprove={buildReview.onSalesApprove}
        onCustomerSigned={buildReview.onCustomerSigned}
        onFinalApproval={buildReview.onFinalApproval}
      />

    </div>
  );
}
